import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { SortableTableHead, getNextSort, type SortDirection } from "@/components/shared/SortableTableHead";
import { TableHeadWithTooltip } from "@/components/shared/TableHeadWithTooltip";
import { ExpandCollapseToggle } from "@/components/shared/ExpandCollapseToggle";
import { TabHeader } from "@/components/shared/TabHeader";
import { BI_TOOLTIPS } from "./tooltips";
import {
  NestedDrawerContainer,
  NestedDrawerTableHeaderRow,
  NestedDrawerTableBodyRow,
  expandedParentRowClassName,
} from "@/components/shared/NestedDrawerTable";
import { cn } from "@/lib/utils";
import {
  useGetBrandIntelligenceCompetitors,
  useGetBrandIntelligenceCompetitorDrawer,
  useGetBrandIntelligenceConfig,
  type BiCompetitorRow,
} from "@/resources/brand-intelligence/brand-intelligence.api";

const cellCls = "px-4 py-3";
const fmtSov = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(2)}%`);
const fmtUsd = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? "—" : `$${n.toFixed(2)}`);

// Section 2.4 price segment relative to the tracked brand's anchor price.
function SegmentBadge({ segment }: { segment: string }) {
  if (segment !== "Competitive" && segment !== "Broad") {
    return <span className="text-xs text-muted-foreground">{segment}</span>;
  }
  return (
    <Badge
      className={cn(
        "text-foreground font-semibold border-0",
        segment === "Competitive" ? "bg-[hsl(185_55%_88%)]" : "bg-[hsl(261_100%_93%)]",
      )}
    >
      {segment}
    </Badge>
  );
}

function ExpandedCompetitors({
  brandId,
  leafCategoryId,
  trackedBrand,
  segmentFilter,
}: {
  brandId: number;
  leafCategoryId: string;
  trackedBrand: string;
  segmentFilter: string;
}) {
  const { data, isLoading, isError } = useGetBrandIntelligenceCompetitorDrawer(brandId, leafCategoryId);
  const [showAll, setShowAll] = useState(false);
  const [sortField, setSortField] = useState<"brand" | "segment" | "sov" | "asins" | "avgPrice" | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const dsort = (f: "brand" | "segment" | "sov" | "asins" | "avgPrice") => {
    const n = getNextSort(sortField, sortDir, f);
    setSortField(n.field);
    setSortDir(n.direction);
  };

  if (isLoading) return <div className="px-4 py-4 text-xs text-muted-foreground">Loading competitors…</div>;
  if (isError || !data) return <div className="px-4 py-4 text-xs text-status-error-strong">Could not load competitors.</div>;

  let rows = data.competitors.filter((c) => c.brand !== trackedBrand && c.brand !== "Other");
  if (segmentFilter !== "all") {
    const target = segmentFilter === "competitive" ? "Competitive" : "Broad";
    rows = rows.filter((c) => c.segment === target);
  }
  if (rows.length === 0) return <div className="px-4 py-4 text-xs text-muted-foreground">No competitors match.</div>;

  if (sortField) {
    const dir = sortDir === "asc" ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      switch (sortField) {
        case "brand": return dir * a.brand.localeCompare(b.brand);
        case "segment": return dir * a.segment.localeCompare(b.segment);
        case "sov": return dir * (a.sov_pct - b.sov_pct);
        case "asins": return dir * (a.asin_count - b.asin_count);
        case "avgPrice": return dir * ((a.avg_price ?? -Infinity) - (b.avg_price ?? -Infinity));
        default: return 0;
      }
    });
  }

  const visible = showAll ? rows : rows.slice(0, 5);

  return (
    <NestedDrawerContainer>
      <Table className="table-fixed">
        <colgroup>
          <col style={{ width: "22%" }} />
          <col style={{ width: "15.6%" }} />
          <col style={{ width: "15.6%" }} />
          <col style={{ width: "15.6%" }} />
          <col style={{ width: "15.6%" }} />
          <col style={{ width: "15.6%" }} />
        </colgroup>
        <TableHeader>
          <NestedDrawerTableHeaderRow>
            <SortableTableHead field="brand" activeField={sortField} direction={sortDir} onSort={dsort} className={cellCls}>Competitor</SortableTableHead>
            <SortableTableHead field="segment" activeField={sortField} direction={sortDir} onSort={dsort} tooltip={BI_TOOLTIPS.segment} className={cellCls}>Segment</SortableTableHead>
            <SortableTableHead field="sov" activeField={sortField} direction={sortDir} onSort={dsort} className={cellCls}>SOV</SortableTableHead>
            <TableHeadWithTooltip tooltip={BI_TOOLTIPS.trendMoM} className={cellCls}>Trend (MoM)</TableHeadWithTooltip>
            <SortableTableHead field="asins" activeField={sortField} direction={sortDir} onSort={dsort} className={cellCls}>ASINs</SortableTableHead>
            <SortableTableHead field="avgPrice" activeField={sortField} direction={sortDir} onSort={dsort} className={cellCls}>Avg price</SortableTableHead>
          </NestedDrawerTableHeaderRow>
        </TableHeader>
        <TableBody>
          {visible.map((c) => (
            <NestedDrawerTableBodyRow key={c.brand}>
              <TableCell className={cn(cellCls, "font-medium")}>{c.brand}</TableCell>
              <TableCell className={cellCls}>
                <SegmentBadge segment={c.segment} />
              </TableCell>
              <TableCell className={cn(cellCls, "tabular-nums font-semibold")}>{fmtSov(c.sov_pct)}</TableCell>
              <TableCell className={cellCls}>
                {c.trend === "up" ? (
                  <TrendingUp className="h-4 w-4 text-trend-positive" aria-label="Up" />
                ) : c.trend === "down" ? (
                  <TrendingDown className="h-4 w-4 text-trend-negative" aria-label="Down" />
                ) : (
                  <Minus className="h-4 w-4 text-trend-neutral" aria-label="Flat" />
                )}
              </TableCell>
              <TableCell className={cn(cellCls, "tabular-nums")}>{c.asin_count}</TableCell>
              <TableCell className={cn(cellCls, "tabular-nums text-muted-foreground")}>{fmtUsd(c.avg_price)}</TableCell>
            </NestedDrawerTableBodyRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 5 && (
        <div className="mt-2 px-4">
          <ExpandCollapseToggle
            expanded={showAll}
            onToggle={() => setShowAll((v) => !v)}
            expandLabel={`Show all ${rows.length} competitors`}
            collapseLabel="Show fewer"
          />
        </div>
      )}
    </NestedDrawerContainer>
  );
}

type SortField = "category" | "competitors" | "brandRank" | "brandSov" | "topCompetitor" | "gap";

export function CompetitorsTab({ brandId }: { brandId: number }) {
  const { data, isLoading, isError } = useGetBrandIntelligenceCompetitors(brandId);
  // The large-SOV-gap threshold is an admin setting; show/filter with its value, not a hardcoded 5.
  const { data: biConfig } = useGetBrandIntelligenceConfig();
  const largeGapPp = biConfig?.large_gap_pp ?? 5;
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [segmentFilter, setSegmentFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const handleSort = (field: SortField) => {
    const next = getNextSort(sortField, sortDir, field);
    setSortField(next.field);
    setSortDir(next.direction);
  };

  const rows = useMemo(() => data?.categories ?? [], [data]);
  const leads = useMemo(() => rows.filter((r) => r.brand_rank === 1), [rows]);

  const filtered = useMemo(() => {
    let d = [...rows];
    const q = search.trim().toLowerCase();
    if (q) d = d.filter((r) => r.name.toLowerCase().includes(q) || (r.top_competitor ?? "").toLowerCase().includes(q));
    const gap = (r: BiCompetitorRow) => r.sov_gap ?? 0;
    if (categoryFilter === "leads") d = d.filter((r) => r.brand_rank === 1);
    else if (categoryFilter === "gap-gt") d = d.filter((r) => r.brand_rank !== 1 && gap(r) > largeGapPp);
    else if (categoryFilter === "gap-lte") d = d.filter((r) => r.brand_rank !== 1 && gap(r) <= largeGapPp && gap(r) > 0);
    else if (categoryFilter === "no-sov") d = d.filter((r) => r.brand_sov === 0);
    if (sortField) {
      const dir = sortDir === "asc" ? 1 : -1;
      d.sort((a, b) => {
        switch (sortField) {
          case "category": return dir * a.name.localeCompare(b.name);
          case "competitors": return dir * (a.competitors - b.competitors);
          case "brandRank": return dir * ((a.brand_rank ?? 1e9) - (b.brand_rank ?? 1e9));
          case "brandSov": return dir * (a.brand_sov - b.brand_sov);
          case "topCompetitor": return dir * (a.top_competitor ?? "").localeCompare(b.top_competitor ?? "");
          case "gap": return dir * ((a.sov_gap ?? 0) - (b.sov_gap ?? 0));
          default: return 0;
        }
      });
    }
    return d;
  }, [rows, search, categoryFilter, sortField, sortDir, largeGapPp]);

  if (isLoading) return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-muted-foreground">Loading competitors…</div>;
  if (isError || !data) return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-status-error-strong">Could not load competitor landscape.</div>;
  if (!data.built || rows.length === 0) {
    return <div className="rounded-sm border border-dashed py-12 text-center text-sm text-muted-foreground">Competitor share of voice hasn't been built yet. Use Refresh to build it.</div>;
  }

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total categories tracked</p>
            <p className="text-3xl font-semibold text-foreground mt-2 tabular-nums">{data.kpis.categories_tracked}</p>
            <p className="text-xs text-muted-foreground mt-1">with competitor data</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Categories where brand leads</p>
            <p className="text-3xl font-semibold text-foreground mt-2 tabular-nums">{data.kpis.categories_leading}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {leads.length > 0 ? (
                leads.map((r) => (
                  <Badge key={r.leaf_category_id} variant="secondary">
                    {r.name}
                  </Badge>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">None</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Categories with large SOV gap</p>
            <p className="text-3xl font-semibold text-foreground mt-2 tabular-nums">{data.kpis.large_gap_categories}</p>
            <p className="text-xs text-muted-foreground mt-1">&gt;{largeGapPp}% gap to nearest competitor</p>
          </CardContent>
        </Card>
      </div>

      <TabHeader
        title="Competitors by Category"
        description="Category-level competitive share of voice for the brand."
        showDescription
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        placeholder="Search categories or competitors"
        filterColumns={[
          {
            id: "category",
            label: "Category status",
            options: [
              { value: "leads", label: "Brand leads" },
              { value: "gap-gt", label: `Gap > ${largeGapPp}%` },
              { value: "gap-lte", label: `Gap ≤ ${largeGapPp}%` },
              { value: "no-sov", label: "No SOV" },
            ],
          },
          {
            id: "segment",
            label: "Competitor segment",
            options: [
              { value: "competitive", label: "Competitive" },
              { value: "broad", label: "Broad" },
            ],
          },
        ]}
        activeFilters={{
          category: categoryFilter === "all" ? [] : [categoryFilter],
          segment: segmentFilter === "all" ? [] : [segmentFilter],
        }}
        onFiltersChange={(f) => {
          setCategoryFilter(f.category?.[0] ?? "all");
          setSegmentFilter(f.segment?.[0] ?? "all");
        }}
      >
        <div className="ml-auto text-sm text-muted-foreground">
          {filtered.length} of {rows.length} categories
        </div>
      </SearchFilterBar>

      {/* Table */}
      <div className="rounded-sm border-y border-border bg-card">
        <Table className="table-fixed">
          <colgroup>
            <col style={{ width: "22%" }} />
            <col style={{ width: "15.6%" }} />
            <col style={{ width: "15.6%" }} />
            <col style={{ width: "15.6%" }} />
            <col style={{ width: "15.6%" }} />
            <col style={{ width: "15.6%" }} />
          </colgroup>
          <TableHeader>
            <TableRow>
              <SortableTableHead field="category" activeField={sortField} direction={sortDir} onSort={handleSort} className={cellCls}>
                Category
              </SortableTableHead>
              <SortableTableHead field="competitors" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="Distinct brands whose ASINs rank in this category's keyword set. Click a row to see them." className={cn(cellCls, "text-right")}>
                Competitors
              </SortableTableHead>
              <SortableTableHead field="brandRank" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="The brand's SOV rank vs. all competing brands in this category." className={cn(cellCls, "text-right")}>
                Brand SOV Rank
              </SortableTableHead>
              <SortableTableHead field="brandSov" activeField={sortField} direction={sortDir} onSort={handleSort} className={cn(cellCls, "text-right")}>
                Brand SOV
              </SortableTableHead>
              <SortableTableHead field="topCompetitor" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="The highest-SOV competing brand in this category." className={cellCls}>
                Top Competitor
              </SortableTableHead>
              <SortableTableHead field="gap" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip="Share-of-voice gap between the brand and the #1 competitor." className={cellCls}>
                SOV Gap to #1
              </SortableTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className={cn(cellCls, "text-center text-muted-foreground py-8")}>
                  No categories match the current filters
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((r) => {
                const isOpen = expanded === r.leaf_category_id;
                const isLead = r.brand_rank === 1;
                const categorySize = r.competitors + 1;
                const ChevronIcon = isOpen ? ChevronDown : ChevronRight;
                return (
                  <Fragment key={r.leaf_category_id}>
                    <TableRow className={cn("cursor-pointer", isOpen && expandedParentRowClassName)} onClick={() => setExpanded(isOpen ? null : r.leaf_category_id)}>
                      <TableCell className={cn(cellCls, "font-medium")}>
                        <div className="flex items-center gap-2">
                          <ChevronIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                          {r.name}
                        </div>
                      </TableCell>
                      <TableCell className={cn(cellCls, "tabular-nums text-right")}>{r.competitors}</TableCell>
                      <TableCell className={cn(cellCls, "text-right")}>
                        <div className={cn("font-semibold tabular-nums", isLead ? "text-trend-positive" : "text-trend-negative")}>
                          {/* AUT-316: a brand that doesn't rank (brand_sov 0) shows rank 0, not a dash, per the prototype. */}
                          #{r.brand_rank ?? 0} <span className="font-normal text-muted-foreground">of {categorySize}</span>
                        </div>
                      </TableCell>
                      <TableCell className={cn(cellCls, "font-semibold tabular-nums text-right", isLead ? "text-trend-positive" : "text-trend-negative")}>
                        {fmtSov(r.brand_sov)}
                      </TableCell>
                      <TableCell className={cellCls}>
                        <span className="text-foreground">{r.top_competitor ?? "—"}</span>{" "}
                        {r.top_competitor_sov != null && <span className="text-muted-foreground tabular-nums">({fmtSov(r.top_competitor_sov)})</span>}
                      </TableCell>
                      <TableCell className={cellCls}>
                        {isLead || (r.sov_gap ?? 0) <= 0 ? (
                          <span className="text-trend-positive font-semibold">Leading</span>
                        ) : (
                          <>
                            <span className="text-trend-negative font-semibold tabular-nums">{r.sov_gap?.toFixed(1)}%</span>{" "}
                            <span className="text-muted-foreground">behind {r.top_competitor}</span>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={6} className="p-0">
                          <ExpandedCompetitors brandId={brandId} leafCategoryId={r.leaf_category_id} trackedBrand={data.brand} segmentFilter={segmentFilter} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
